import SectionHeader from "./components/SectionHeader";
import Simulator from "./components/Simulator";
import {
  agents,
  assumptions,
  fundUse,
  marketPoints,
  problemPoints,
  progressPoints,
  projectionHeaders,
  projections,
  revenueLines,
  revenueMix,
  risks,
  roadmap,
  teamRoles,
  text,
  trends,
} from "./content";
import { ArrowIcon, CheckIcon, LayersIcon, ShieldIcon } from "./icons";
import type { Locale } from "./types";

type SectionProps = {
  locale: Locale;
};

export function HeroSection({ locale }: SectionProps) {
  return (
    <section id="top" className="hero-section deck-section" data-section="01">
      <div className="hero-grid page-shell">
        <div className="hero-copy">
          <h1>AgentNexus</h1>
          <p className="hero-cn-name">{locale === "zh" ? "代理枢纽" : "Agent Hub"}</p>
          <p className="hero-positioning">
            {locale === "zh"
              ? "全球首个真正「AI Agent 原生」的加密货币交易所"
              : "The world’s first truly “AI Agent-native” cryptocurrency exchange"}
          </p>
          <p className="hero-promise">
            {locale === "zh"
              ? "你只负责说目标，AI负责全部执行"
              : "You state the goal. AI handles the entire execution."}
          </p>
          <div className="financing-meta">
            <strong>{locale === "zh" ? "种子轮／Pre-A轮融资" : "Seed / Pre-Series A Financing"}</strong>
            <span>{locale === "zh" ? "融资额：______ 万美元" : "Funding Amount: ______ × US$10,000"}</span>
            <span>{locale === "zh" ? "2026年7月" : "July 2026"}</span>
          </div>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="operator-halo" />
          <img src="/anx-agent-team-v2.png" alt="" />
          <div className="operator-scanline" />
          <span className="operator-node node-a" />
          <span className="operator-node node-b" />
          <span className="operator-node node-c" />
        </div>
      </div>
      <a className="scroll-cue" href="#problem" aria-label={locale === "zh" ? "前往下一页" : "Go to the next page"}>
        <span>01</span>
        <ArrowIcon />
      </a>
    </section>
  );
}

export function ProblemSection({ locale }: SectionProps) {
  return (
    <section id="problem" className="content-section deck-section problem-section" data-section="02">
      <div className="page-shell split-layout">
        <SectionHeader
          locale={locale}
          number="02"
          title={text("我们解决什么问题？", "What problem do we solve?")}
          intro={text("现在的加密交易所对普通人太难了：", "Today’s cryptocurrency exchanges are too difficult for ordinary users:")}
        />
        <ol className="numbered-list problem-list">
          {problemPoints.map((point, index) => (
            <li key={point.en}>
              <span>0{index + 1}</span>
              <p>{point[locale]}</p>
            </li>
          ))}
        </ol>
        <div className="section-statement full-span">
          <small>{locale === "zh" ? "一句话：" : "In one sentence:"}</small>
          <strong>
            {locale === "zh"
              ? "现在的交易所是专业工具，不是普通人的助手。"
              : "Today’s exchanges are professional tools, not assistants for ordinary users."}
          </strong>
        </div>
      </div>
    </section>
  );
}

export function SolutionSection({ locale }: SectionProps) {
  return (
    <section id="solution" className="content-section deck-section solution-section" data-section="03">
      <div className="page-shell">
        <SectionHeader
          locale={locale}
          number="03"
          title={text("我们的解决方案", "Our solution")}
          intro={text("AgentNexus = 用说话的方式完成交易", "AgentNexus = Trade simply by stating your goal")}
        />
        <div className="solution-grid">
          <div className="intent-example">
            <small>{locale === "zh" ? "用户只需用中文输入目标，例如：" : "Users only need to enter a goal in Chinese, for example:"}</small>
            <blockquote>
              {locale === "zh"
                ? "「把5,000 USDC自动找最高收益，最大回撤不超过3%」"
                : "“Automatically find the highest yield for 5,000 USDC, with a maximum drawdown of no more than 3%.”"}
            </blockquote>
          </div>
          <div className="agent-system">
            <div className="agent-core">
              <span>AI</span>
              <small>AgentNexus</small>
            </div>
            <p>{locale === "zh" ? "系统自动派出4个AI Agent：" : "The system automatically deploys four AI Agents:"}</p>
            <div className="agent-grid">
              {agents.map((agent, index) => (
                <div key={agent.en}>
                  <span>0{index + 1}</span>
                  <strong>{agent[locale]}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="transformation-line">
          {locale === "zh" ? (
            <>
              <small>从</small>
              <span>「人操作机器」</span>
              <ArrowIcon />
              <small>变成</small>
              <strong>「人指挥AI团队」。</strong>
            </>
          ) : (
            <>
              <small>The model shifts from</small>
              <span>“people operating machines”</span>
              <ArrowIcon />
              <small>to</small>
              <strong>“people directing an AI team.”</strong>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function ProductSection({ locale }: SectionProps) {
  return (
    <section id="product" className="content-section deck-section product-section" data-section="04">
      <div className="page-shell">
        <SectionHeader
          locale={locale}
          number="04"
          title={text("产品怎么用？（简单演示）", "How does the product work? (Simple demo)")}
        />
        <Simulator locale={locale} />
        <p className="section-footline">
          {locale === "zh"
            ? "界面像跟AI聊天一样简单，背后是完整的交易系统。"
            : "The interface is as simple as chatting with AI, backed by a complete trading system."}
        </p>
      </div>
    </section>
  );
}

export function WhyNowSection({ locale }: SectionProps) {
  return (
    <section id="why-now" className="content-section deck-section why-now-section" data-section="05">
      <div className="page-shell">
        <SectionHeader
          locale={locale}
          number="05"
          title={text("为什么现在做？", "Why now?")}
          intro={text("2026年三大趋势正在汇合：", "Three major trends are converging in 2026:")}
        />
        <div className="trend-convergence">
          {trends.map((trend, index) => (
            <article key={trend.en}>
              <span>0{index + 1}</span>
              <p>{trend[locale]}</p>
            </article>
          ))}
          <div className="convergence-core">
            <LayersIcon />
            <strong>AgentNexus</strong>
          </div>
        </div>
        <p className="section-footline">
          {locale === "zh"
            ? "AgentNexus正好站在这三个趋势的交叉点。"
            : "AgentNexus sits precisely at the intersection of these three trends."}
        </p>
      </div>
    </section>
  );
}

export function MarketSection({ locale }: SectionProps) {
  return (
    <section id="market" className="content-section deck-section market-section" data-section="06">
      <div className="page-shell">
        <SectionHeader locale={locale} number="06" title={text("市场有多大？", "How large is the market?")} />
        <div className="market-rails">
          {marketPoints.map((point, index) => (
            <div key={point.en}>
              <span>{index === 0 ? "亿级" : index === 1 ? "<5%" : "→"}</span>
              <p>{point[locale]}</p>
            </div>
          ))}
        </div>
        <div className="target-audience">
          <small>{locale === "zh" ? "我们的目标：" : "Our target users:"}</small>
          <strong>
            {locale === "zh"
              ? "「想参与加密，但不想自己天天盯盘」的普通投资者与中小机构。"
              : "Ordinary investors and small and medium-sized institutions that want to participate in crypto without monitoring markets every day."}
          </strong>
        </div>
        <p className="section-footline">
          {locale === "zh"
            ? "这是一个从专业工具走向大众助手的巨大机会。"
            : "This is a major opportunity to move from professional tools to assistants for ordinary users."}
        </p>
      </div>
    </section>
  );
}

export function BusinessSection({ locale }: SectionProps) {
  return (
    <section id="business" className="content-section deck-section business-section" data-section="07">
      <div className="page-shell">
        <SectionHeader locale={locale} number="07" title={text("我们怎么赚钱？", "How do we make money?")} />
        <ol className="revenue-list">
          {revenueLines.map((line, index) => (
            <li key={line.en}>
              <span>0{index + 1}</span>
              <p>{line[locale]}</p>
            </li>
          ))}
        </ol>
        <div className="os-vision">
          <span>AgentNexus</span>
          <ArrowIcon />
          <strong>{locale === "zh" ? "长期可发展成AI金融操作系统。" : "Over the long term, AgentNexus can evolve into an AI financial operating system."}</strong>
        </div>
      </div>
    </section>
  );
}

export function CompetitionSection({ locale }: SectionProps) {
  return (
    <section id="competition" className="content-section deck-section competition-section" data-section="08">
      <div className="page-shell">
        <SectionHeader locale={locale} number="08" title={text("竞争优势", "Competitive advantage")} />
        <div className="comparison-grid">
          <article>
            <small>{locale === "zh" ? "vs 传统交易所：" : "vs. Traditional exchanges:"}</small>
            <h3>{locale === "zh" ? "他们是工具，我们是助手。" : "They are tools; we are an assistant."}</h3>
            <p>{locale === "zh" ? "用户不用会操作，只要会说话。" : "Users do not need to know how to operate it; they only need to speak."}</p>
          </article>
          <article>
            <small>{locale === "zh" ? "vs 普通交易机器人：" : "vs. Conventional trading bots:"}</small>
            <h3>{locale === "zh" ? "机器人只会执行固定规则。" : "Bots only execute fixed rules."}</h3>
            <p>{locale === "zh" ? "我们的Agent会思考、协调、动态调整。" : "Our Agents reason, coordinate, and dynamically adjust."}</p>
          </article>
        </div>
        <div className="moat-statement">
          <ShieldIcon />
          <div>
            <small>{locale === "zh" ? "核心护城河：" : "Core moat:"}</small>
            <strong>
              {locale === "zh"
                ? "最早把AI Agent身份、意图交易、跨链抽象完整结合的产品。"
                : "One of the earliest products to fully integrate AI Agent identity, intent-based trading, and cross-chain abstraction."}
            </strong>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RoadmapSection({ locale }: SectionProps) {
  return (
    <section id="roadmap" className="content-section deck-section roadmap-section" data-section="09">
      <div className="page-shell">
        <SectionHeader locale={locale} number="09" title={text("产品路线图", "Product roadmap")} />
        <ol className="roadmap-line">
          {roadmap.map((item, index) => (
            <li key={item.time.en}>
              <span>0{index + 1}</span>
              <time>{item.time[locale]}</time>
              <p>{item.body[locale]}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function ProgressSection({ locale }: SectionProps) {
  return (
    <section id="progress" className="content-section deck-section progress-section" data-section="10">
      <div className="page-shell split-layout">
        <SectionHeader locale={locale} number="10" title={text("目前进度", "Current progress")} />
        <ul className="progress-list">
          {progressPoints.map((point) => (
            <li key={point.en}>
              <CheckIcon />
              <span>{point[locale]}</span>
            </li>
          ))}
        </ul>
        <div className="next-step full-span">
          <small>{locale === "zh" ? "下一步：" : "Next step:"}</small>
          <strong>{locale === "zh" ? "快速进入测试网验证。" : "Move rapidly into testnet validation."}</strong>
          <ArrowIcon />
        </div>
      </div>
    </section>
  );
}

export function TeamSection({ locale }: SectionProps) {
  return (
    <section id="team" className="content-section deck-section team-section" data-section="11">
      <div className="page-shell">
        <SectionHeader locale={locale} number="11" title={text("团队", "Team")} />
        <div className="team-grid">
          {teamRoles.map((role, index) => {
            const [title, detail] = role[locale].split(locale === "zh" ? "：" : ": ");
            return (
              <article key={role.en}>
                <span>0{index + 1}</span>
                <h3>{title}{locale === "zh" ? "：" : ":"}</h3>
                <p>{detail}</p>
              </article>
            );
          })}
        </div>
        <p className="team-note">
          {locale === "zh" ? "（可根据实际情况替换补充）" : "(To be replaced or supplemented based on the actual team.)"}
        </p>
      </div>
    </section>
  );
}

export function FinancialsSection({ locale }: SectionProps) {
  return (
    <section id="financials" className="content-section deck-section financials-section" data-section="12">
      <div className="page-shell">
        <SectionHeader locale={locale} number="12" title={text("财务展望与关键指标", "Financial outlook and key metrics")} />
        <div className="validation-objective">
          <small>{locale === "zh" ? "核心验证目标" : "Core Validation Objective"}</small>
          <strong>
            {locale === "zh"
              ? "先证明一件事：用户真的愿意用自然语言指挥 AI Agent 完成交易，并愿意为此付费。"
              : "First, prove one thing: users are genuinely willing to direct AI Agents through natural language to complete trades—and are willing to pay for it."}
          </strong>
        </div>

        <h3 className="financial-subtitle">{locale === "zh" ? "未来24个月简单预测（示例）" : "Simplified 24-Month Forecast (Illustrative)"}</h3>
        <div className="projection-table-wrap">
          <table className="projection-table">
            <thead>
              <tr>
                {projectionHeaders.map((header) => <th key={header.en}>{header[locale]}</th>)}
              </tr>
            </thead>
            <tbody>
              {projections.map((row) => (
                <tr key={row.timeline.en}>
                  <td>{row.timeline[locale]}</td>
                  <td>{row.users[locale]}</td>
                  <td>{row.volume[locale]}</td>
                  <td>{row.revenue[locale]}</td>
                  <td>{row.goal[locale]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="financial-details">
          <div>
            <h3>{locale === "zh" ? "收入结构预估（成熟期）" : "Estimated Revenue Mix at Maturity"}</h3>
            <div className="revenue-mix">
              {revenueMix.map((item) => (
                <div key={item.label.en}>
                  <p><span>{item.label[locale]}{locale === "zh" ? "：" : ":"}</span><strong>{locale === "zh" ? `约${item.value}` : `approximately ${item.value}`}</strong></p>
                  <span className="mix-track"><i style={{ width: `${item.size}%` }} /></span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3>{locale === "zh" ? "关键假设（通俗说明）" : "Key Assumptions in Plain Language"}</h3>
            <ul className="assumption-list">
              {assumptions.map((assumption) => <li key={assumption.en}>{assumption[locale]}</li>)}
            </ul>
          </div>
        </div>

        <div className="important-note">
          <small>{locale === "zh" ? "重要提醒" : "Important Note"}</small>
          <p>{locale === "zh" ? "以上数字为早期规划参考，实际会根据测试网数据快速调整。" : "These figures are references for early-stage planning and will be adjusted rapidly based on testnet data."}</p>
          <p>{locale === "zh" ? "我们目前最重视的不是「看起来很大的数字」，而是先跑通「用户愿意用、愿意留、愿意付费」这个闭环。" : "Our priority is to prove that users are willing to use the product, stay, and pay."}</p>
        </div>
      </div>
    </section>
  );
}

export function FundingSection({ locale }: SectionProps) {
  return (
    <section id="funding" className="content-section deck-section funding-section" data-section="13">
      <div className="page-shell">
        <SectionHeader locale={locale} number="13" title={text("融资需求与用途", "Funding request and use of proceeds")} />
        <div className="funding-grid">
          <div className="funding-ask">
            <small>{locale === "zh" ? "本轮融资：" : "Funding Sought in This Round:"}</small>
            <strong>{locale === "zh" ? "______ 万美元" : "______ × US$10,000"}</strong>
          </div>
          <div>
            <h3>{locale === "zh" ? "资金用途：" : "Use of Proceeds:"}</h3>
            <div className="fund-use-list">
              {fundUse.map((item) => (
                <div key={item.label.en}>
                  <strong>{item.value}%</strong>
                  <span>{item.label[locale]}</span>
                  <i><b style={{ width: `${item.value}%` }} /></i>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="section-footline">
          {locale === "zh"
            ? "目标：12-18个月内把产品做到真正可用，并拥有真实用户与交易量。"
            : "Goal: Build a genuinely usable product within 12–18 months and establish real users and trading volume."}
        </p>
      </div>
    </section>
  );
}

export function RisksSection({ locale }: SectionProps) {
  return (
    <section id="risks" className="content-section deck-section risks-section" data-section="14">
      <div className="page-shell split-layout">
        <SectionHeader locale={locale} number="14" title={text("风险与挑战（诚实说明）", "Risks and challenges (an honest assessment)")} />
        <ol className="risk-list">
          {risks.map((risk, index) => (
            <li key={risk.en}>
              <span>0{index + 1}</span>
              <p>{risk[locale]}</p>
            </li>
          ))}
        </ol>
        <div className="risk-response full-span">
          <ShieldIcon />
          <strong>
            {locale === "zh"
              ? "我们会用小额测试、严格风控、逐步开放来降低风险。"
              : "We will reduce these risks through small-value testing, strict risk controls, and opening access step by step."}
          </strong>
        </div>
      </div>
    </section>
  );
}

export function ClosingSection({ locale }: SectionProps) {
  return (
    <section id="closing" className="closing-section deck-section" data-section="15">
      <div className="page-shell closing-grid">
        <div className="closing-copy">
          <span className="section-number">15</span>
          <p className="closing-intro">{locale === "zh" ? "我们相信未来：" : "We believe that in the future:"}</p>
          <h2>
            {locale === "zh" ? (
              <>
                <span>不是每个人都要成为交易高手，</span>
                <span>而是每个人都能拥有一个专属的AI交易团队。</span>
              </>
            ) : (
              <>
                <span>Not everyone will need to become an expert trader.</span>
                <span>Instead, everyone will be able to have a dedicated AI trading team.</span>
              </>
            )}
          </h2>
          <div className="closing-brand">
            <strong>AgentNexus</strong>
            <p>{locale === "zh" ? "用自然语言指挥AI，完成你的投资目标" : "Direct AI through natural language to achieve your investment goals."}</p>
          </div>
          <p className="closing-call">{locale === "zh" ? "一起把这个未来做成现实。" : "Let’s make this future a reality together."}</p>
          <address>
            <small>{locale === "zh" ? "联系方式：" : "Contact:"}</small>
            <span>{locale === "zh" ? "姓名／职位" : "Name / Title"}</span>
            <span>{locale === "zh" ? "邮箱／Telegram／微信" : "Email / Telegram / WeChat"}</span>
          </address>
        </div>
        <div className="closing-visual" aria-hidden="true">
          <img src="/anx-agent-team-v2.png" alt="" />
          <div className="closing-orbit" />
        </div>
      </div>
    </section>
  );
}
